import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../api';

/**
 * Human Resources landing — EXACTLY nine cards (Round HR-PA, Part 2).
 *
 * The owner's structure, one level deep, no card-hub-behind-a-card:
 *
 *   Add Employee · Manage Employee · FTO · COA · Reports ·
 *   Punch Editor · Late In & Early Out · Profile Review · Dashboard
 *
 * Every card opens its WORKSPACE directly (?section=…). Anything that is
 * not one of the nine (Leave legacy area, Web forms, Audit/More, the
 * combined Review queue) stays reachable through the small "More tools"
 * links under the grid — features were MOVED, never removed (Part 3).
 *
 * The pending badges come from ONE fail-silent call to the existing
 * review-queue endpoint; the landing renders instantly without it.
 */
const CARDS = [
    { key: 'add-employee',   title: 'Add Employee',        icon: 'ri-user-add-line',        to: '/humanresource?section=employees&view=add',  desc: 'Create a new employee record.' },
    { key: 'manage-employee',title: 'Manage Employee',     icon: 'ri-team-line',            to: '/humanresource?section=employees&view=list', desc: 'Search, edit, deactivate, reactivate.' },
    { key: 'fto',            title: 'FTO',                 icon: 'ri-calendar-2-line',      to: '/humanresource?section=fto',                 desc: 'Flexible Time Off — add, remove, holidays, approvals, summary.', badge: 'fto' },
    { key: 'coa',            title: 'COA',                 icon: 'ri-calendar-check-line',  to: '/humanresource?section=coa',                 desc: 'Change of Attendance — add, remove, approvals, history.', badge: 'coa' },
    { key: 'reports',        title: 'Reports',             icon: 'ri-file-chart-line',      to: '/humanresource?section=reports',             desc: 'FTO, COA, payroll & punch records, analytics.' },
    { key: 'punch-editor',   title: 'Punch Editor',        icon: 'ri-time-line',            to: '/humanresource?section=punches',             desc: 'Find an employee’s punches and fix them.' },
    // Owner 2026-10-15: "put this module in HR also" — the same Team Work Hours screen.
    { key: 'team-hours',     title: 'Team Work Hours',     icon: 'ri-time-fill',            to: '/attendance/team/work-hours',                desc: 'Set a hourly employee’s own daily hours (7:30 to 4:00, 7 to 3).' },
    { key: 'latein',         title: 'Late In & Early Out', icon: 'ri-alarm-warning-line',   to: '/humanresource?section=latein',              desc: 'Daily exceptions and the summary report.' },
    { key: 'profiles',       title: 'Profile Review',      icon: 'ri-user-follow-line',     to: '/humanresource?section=profiles',            desc: 'Approve employee profile changes.', badge: 'profile' },
    { key: 'dashboard',      title: 'Dashboard',           icon: 'ri-dashboard-2-line',     to: '/humanresource?section=dashboard',           desc: 'Headcount, data quality, quick actions.' },
];

const HrLandingCards = () => {
    const [counts, setCounts] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get('dashboard/hr/review-queue')
            .then((r) => {
                if (!alive) return;
                const items = Array.isArray(r.data?.items) ? r.data.items
                    : Array.isArray(r.data) ? r.data : [];
                const c = { fto: 0, coa: 0, profile: 0 };
                items.forEach((it) => {
                    const k = String(it.kind || '').toLowerCase();
                    if (k === 'fto') c.fto += 1;
                    else if (k === 'coa') c.coa += 1;
                    else if (k === 'profile') c.profile += 1;
                });
                setCounts(c);
            })
            .catch(() => { /* fail silent — badges are a bonus */ });
        return () => { alive = false; };
    }, []);

    return (
        <div className="py-1">
            <div className="hr-landing-grid" role="list">
                {CARDS.map((c) => {
                    const badge = c.badge && counts ? counts[c.badge] : 0;
                    return (
                        <Link key={c.key} to={c.to} className="hr-landing-card" role="listitem">
                            <span className="hr-landing-card-icon"><i className={c.icon} aria-hidden="true" /></span>
                            <span className="hr-landing-card-text">
                                <strong>
                                    {c.title}
                                    {badge > 0 && (
                                        <span className="badge bg-danger ms-2" title={`${badge} pending`}>{badge}</span>
                                    )}
                                </strong>
                                <small>{c.desc}</small>
                            </span>
                            <i className="ri-arrow-right-s-line hr-landing-card-arrow" aria-hidden="true" />
                        </Link>
                    );
                })}
            </div>

            {/* Everything that is not one of the nine stays one click away —
                moved, not removed (spec Part 3). */}
            <div className="text-muted small mt-3 d-flex flex-wrap gap-3 align-items-center">
                <span className="fw-semibold">More tools:</span>
                <Link to="/humanresource?section=review">Review queue (all pending)</Link>
                <Link to="/humanresource?section=leave&area=record">Record absence</Link>
                <Link to="/humanresource?section=webforms">Web forms</Link>
                <Link to="/humanresource?section=more">Overview &amp; reminders</Link>
                {/* Go-live parity (2026-09-24): India accounts (DI / MAFIA Aux logins) — same HR gate as this page. */}
                <Link to="/hr/india-employees">India employees</Link>
            </div>
        </div>
    );
};

export default HrLandingCards;
